import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { PageMeta } from '@/components/seo/PageMeta'
import {
  integrationService,
  type Integration,
  type ConnectIntegrationRequest,
  type IntegrationTestResult,
  type AIModelInfo
} from '@/lib/api/services/integrations'
import {
  Container,
  Heading,
  Text,
  Card,
  Flex,
  Button,
  Badge,
  Switch,
  Grid,
  Dialog,
  TextField,
  Separator,
  Select as RadixSelect
} from '@radix-ui/themes'
import {
  Plug,
  Check,
  AlertCircle,
  Loader2,
  Bot,
  Brain,
  Sparkles,
  HardDrive,
  Cloud,
  CloudCog,
  MessageSquare,
  Users,
  BarChart,
  LineChart,
  CreditCard,
  Zap,
  TestTube,
  X,
  RefreshCw,
  ChevronDown,
  Search,
  Building2,
  ShieldAlert
} from 'lucide-react'
import { useAuth } from '@/lib/contexts/auth-context'
import { Button as ShadcnButton } from '@/components/ui/button'

interface TenantOption {
  id: string
  name: string
}

const categoryLabels: Record<string, string> = {
  storage: 'Storage',
  communication: 'Communication',
  analytics: 'Analytics & Automation',
  ai: 'AI & Machine Learning',
  billing: 'Billing',
  automation: 'Automation',
}

const iconMap: Record<string, React.ReactNode> = {
  Bot: <Bot className="h-6 w-6" />,
  Brain: <Brain className="h-6 w-6" />,
  Sparkles: <Sparkles className="h-6 w-6" />,
  HardDrive: <HardDrive className="h-6 w-6" />,
  Cloud: <Cloud className="h-6 w-6" />,
  CloudCog: <CloudCog className="h-6 w-6" />,
  MessageSquare: <MessageSquare className="h-6 w-6" />,
  Users: <Users className="h-6 w-6" />,
  BarChart: <BarChart className="h-6 w-6" />,
  LineChart: <LineChart className="h-6 w-6" />,
  CreditCard: <CreditCard className="h-6 w-6" />,
  Zap: <Zap className="h-6 w-6" />,
}

// Integration-specific configurations
interface IntegrationFieldConfig {
  primaryField: {
    name: string
    label: string
    placeholder: string
    type: 'text' | 'password'
    helpText?: string
  }
  optionalFields?: Array<{
    name: string
    label: string
    placeholder: string
    helpText?: string
    type?: 'text' | 'password'
  }>
  hasModelSelector?: boolean
  modelFieldLabel?: string
}

const integrationConfigs: Record<string, IntegrationFieldConfig> = {
  openrouter: {
    primaryField: {
      name: 'apiKey',
      label: 'API Key',
      placeholder: 'sk-or-v1-...',
      type: 'password',
      helpText: 'Get your API key from openrouter.ai/keys'
    },
    optionalFields: [
      {
        name: 'baseUrl',
        label: 'Base URL',
        placeholder: 'https://openrouter.ai/api/v1',
        helpText: 'Leave empty to use the default endpoint'
      }
    ],
    hasModelSelector: true,
    modelFieldLabel: 'Default Model'
  },
  openai: {
    primaryField: {
      name: 'apiKey',
      label: 'API Key',
      placeholder: 'sk-...',
      type: 'password',
      helpText: 'Get your API key from platform.openai.com/api-keys'
    },
    optionalFields: [
      {
        name: 'organization',
        label: 'Organization ID',
        placeholder: 'org-...',
        helpText: 'Optional: Your OpenAI organization ID'
      },
      {
        name: 'baseUrl',
        label: 'Base URL',
        placeholder: 'https://api.openai.com/v1',
        helpText: 'Leave empty to use the default endpoint'
      }
    ],
    hasModelSelector: true,
    modelFieldLabel: 'Default Model'
  },
  anthropic: {
    primaryField: {
      name: 'apiKey',
      label: 'API Key',
      placeholder: 'sk-ant-...',
      type: 'password',
      helpText: 'Get your API key from console.anthropic.com'
    },
    optionalFields: [
      {
        name: 'baseUrl',
        label: 'Base URL',
        placeholder: 'https://api.anthropic.com',
        helpText: 'Leave empty to use the default endpoint'
      }
    ],
    hasModelSelector: true,
    modelFieldLabel: 'Default Model'
  },
  slack: {
    primaryField: {
      name: 'webhookUrl',
      label: 'Webhook URL',
      placeholder: 'https://hooks.slack.com/services/...',
      type: 'text',
      helpText: 'Create a webhook at api.slack.com/apps'
    }
  },
  stripe: {
    primaryField: {
      name: 'apiKey',
      label: 'Secret Key',
      placeholder: 'sk_test_... or sk_live_...',
      type: 'password',
      helpText: 'Get your secret key from dashboard.stripe.com/apikeys'
    },
    optionalFields: [
      {
        name: 'publishableKey',
        label: 'Publishable Key',
        placeholder: 'pk_test_... or pk_live_...',
        helpText: 'Required for frontend Stripe.js integration'
      },
      {
        name: 'webhookSecret',
        label: 'Webhook Secret',
        placeholder: 'whsec_...',
        helpText: 'For receiving Stripe webhook events'
      }
    ]
  }
}

interface ConnectModalProps {
  integration: Integration | null
  open: boolean
  onClose: () => void
  onConnect: (config: ConnectIntegrationRequest) => Promise<void>
}

function ConnectModal({ integration, open, onClose, onConnect }: ConnectModalProps) {
  const [formData, setFormData] = useState<Record<string, string>>({})
  const [loading, setLoading] = useState(false)
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<IntegrationTestResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [models, setModels] = useState<AIModelInfo[]>([])
  const [loadingModels, setLoadingModels] = useState(false)
  const [modelSearch, setModelSearch] = useState('')
  const [showModelDropdown, setShowModelDropdown] = useState(false)
  const [customModel, setCustomModel] = useState(false)

  const config = integration?.type ? integrationConfigs[integration.type] : null
  const primaryField = config?.primaryField
  const optionalFields = config?.optionalFields || []
  const hasModelSelector = config?.hasModelSelector

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setFormData({})
      setError(null)
      setTestResult(null)
      setModels([])
      setModelSearch('')
      setCustomModel(false)
    }
  }, [open])

  // Build the request object from form data
  const buildRequest = useCallback((): ConnectIntegrationRequest => {
    const request: ConnectIntegrationRequest = {}

    if (primaryField?.name === 'webhookUrl') {
      request.additionalConfig = { webhookUrl: formData.webhookUrl || '' }
    } else {
      request.apiKey = formData.apiKey
    }

    if (formData.baseUrl) request.baseUrl = formData.baseUrl
    if (formData.defaultModel) request.defaultModel = formData.defaultModel

    // Add additional config fields
    const additionalFields = ['organization', 'publishableKey', 'webhookSecret']
    for (const field of additionalFields) {
      if (formData[field]) {
        request.additionalConfig = request.additionalConfig || {}
        request.additionalConfig[field] = formData[field]
      }
    }

    return request
  }, [formData, primaryField])

  // Test credentials
  const handleTest = async () => {
    if (!integration) return
    setTesting(true)
    setTestResult(null)
    setError(null)

    try {
      const request = buildRequest()
      const result = await integrationService.testCredentials(integration.type, request)
      setTestResult(result)

      // If test was successful and we have a model selector, fetch models
      if (result.isSuccess && hasModelSelector) {
        await fetchModels()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Test failed')
    } finally {
      setTesting(false)
    }
  }

  // Fetch models for AI integrations
  const fetchModels = async () => {
    if (!integration || !hasModelSelector) return
    setLoadingModels(true)

    try {
      const request = buildRequest()
      const fetchedModels = await integrationService.getModels(integration.type, request)
      setModels(fetchedModels)
    } catch (err) {
      console.error('Failed to fetch models:', err)
      // Don't show error, just use empty list
    } finally {
      setLoadingModels(false)
    }
  }

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      const request = buildRequest()
      await onConnect(request)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect')
    } finally {
      setLoading(false)
    }
  }

  const isPrimaryFieldValid = primaryField
    ? (formData[primaryField.name] || '').trim().length > 0
    : false

  // Filter models based on search
  const filteredModels = models.filter(m =>
    m.name.toLowerCase().includes(modelSearch.toLowerCase()) ||
    m.id.toLowerCase().includes(modelSearch.toLowerCase()) ||
    (m.provider?.toLowerCase().includes(modelSearch.toLowerCase()))
  )

  const recommendedModels = filteredModels.filter(m => m.isRecommended)
  const otherModels = filteredModels.filter(m => !m.isRecommended)

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Content maxWidth="500px">
        <Dialog.Title>Connect {integration?.name}</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Enter your credentials to connect this integration.
        </Dialog.Description>

        <form onSubmit={handleSubmit}>
          <Flex direction="column" gap="4">
            {/* Primary Field (API Key or Webhook URL) */}
            {primaryField && (
              <div>
                <Text as="label" size="2" weight="bold" mb="1">
                  {primaryField.label} <span className="text-red-500">*</span>
                </Text>
                <TextField.Root
                  type={primaryField.type}
                  placeholder={primaryField.placeholder}
                  value={formData[primaryField.name] || ''}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, [primaryField.name]: e.target.value }))
                    setTestResult(null) // Reset test result when credentials change
                  }}
                  required
                />
                {primaryField.helpText && (
                  <Text size="1" color="gray" mt="1">
                    {primaryField.helpText}
                  </Text>
                )}
              </div>
            )}

            {/* Test Credentials Button */}
            <Flex gap="2" align="center">
              <Button
                type="button"
                variant="soft"
                onClick={handleTest}
                disabled={!isPrimaryFieldValid || testing}
              >
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Testing...
                  </>
                ) : (
                  <>
                    <TestTube className="h-4 w-4" />
                    Test Credentials
                  </>
                )}
              </Button>
              {testResult && (
                <Badge color={testResult.isSuccess ? 'green' : 'red'} variant="soft">
                  {testResult.isSuccess ? (
                    <>
                      <Check className="h-3 w-3" />
                      Valid
                    </>
                  ) : (
                    <>
                      <AlertCircle className="h-3 w-3" />
                      Invalid
                    </>
                  )}
                </Badge>
              )}
            </Flex>

            {/* Test Result Details */}
            {testResult && !testResult.isSuccess && testResult.errorMessage && (
              <Card className="border-red-200 bg-red-50">
                <Text size="2" color="red">
                  {testResult.errorMessage}
                </Text>
              </Card>
            )}

            {testResult && testResult.isSuccess && testResult.details && (
              <Card className="border-green-200 bg-green-50">
                <Text size="2" color="green">
                  {(testResult.details as Record<string, unknown>).message as string || 'Connection successful!'}
                </Text>
              </Card>
            )}

            {/* Model Selector (for AI integrations) */}
            {hasModelSelector && testResult?.isSuccess && (
              <>
                <Separator size="4" />
                <div>
                  <Text as="label" size="2" weight="bold" mb="1">
                    {config?.modelFieldLabel || 'Default Model'}
                  </Text>

                  {loadingModels ? (
                    <Flex align="center" gap="2" py="2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <Text size="2" color="gray">Loading models...</Text>
                    </Flex>
                  ) : customModel ? (
                    <Flex gap="2">
                      <TextField.Root
                        style={{ flex: 1 }}
                        placeholder="Enter model ID (e.g., openai/gpt-4o)"
                        value={formData.defaultModel || ''}
                        onChange={(e) => setFormData(prev => ({ ...prev, defaultModel: e.target.value }))}
                      />
                      <Button
                        type="button"
                        variant="soft"
                        onClick={() => {
                          setCustomModel(false)
                          setFormData(prev => ({ ...prev, defaultModel: '' }))
                        }}
                      >
                        Select from list
                      </Button>
                    </Flex>
                  ) : (
                    <div className="relative">
                      <div
                        className="flex items-center justify-between p-2 border rounded cursor-pointer hover:bg-gray-50"
                        onClick={() => setShowModelDropdown(!showModelDropdown)}
                      >
                        <Text size="2">
                          {formData.defaultModel
                            ? models.find(m => m.id === formData.defaultModel)?.name || formData.defaultModel
                            : 'Select a model...'}
                        </Text>
                        <ChevronDown className="h-4 w-4 text-gray-500" />
                      </div>

                      {showModelDropdown && (
                        <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-64 overflow-auto">
                          <div className="sticky top-0 bg-white p-2 border-b">
                            <TextField.Root
                              placeholder="Search models..."
                              value={modelSearch}
                              onChange={(e) => setModelSearch(e.target.value)}
                            >
                              <TextField.Slot>
                                <Search className="h-4 w-4" />
                              </TextField.Slot>
                            </TextField.Root>
                          </div>

                          {recommendedModels.length > 0 && (
                            <>
                              <div className="px-2 py-1 bg-gray-50 text-xs font-medium text-gray-500">
                                Recommended
                              </div>
                              {recommendedModels.map(model => (
                                <div
                                  key={model.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, defaultModel: model.id }))
                                    setShowModelDropdown(false)
                                    setModelSearch('')
                                  }}
                                >
                                  <Text size="2" weight="medium">{model.name}</Text>
                                  {model.provider && (
                                    <Text size="1" color="gray" ml="2">({model.provider})</Text>
                                  )}
                                  {model.description && (
                                    <Text size="1" color="gray" as="div">{model.description}</Text>
                                  )}
                                </div>
                              ))}
                            </>
                          )}

                          {otherModels.length > 0 && (
                            <>
                              <div className="px-2 py-1 bg-gray-50 text-xs font-medium text-gray-500">
                                All Models
                              </div>
                              {otherModels.slice(0, 50).map(model => (
                                <div
                                  key={model.id}
                                  className="px-3 py-2 hover:bg-blue-50 cursor-pointer"
                                  onClick={() => {
                                    setFormData(prev => ({ ...prev, defaultModel: model.id }))
                                    setShowModelDropdown(false)
                                    setModelSearch('')
                                  }}
                                >
                                  <Text size="2">{model.name}</Text>
                                  {model.provider && (
                                    <Text size="1" color="gray" ml="2">({model.provider})</Text>
                                  )}
                                </div>
                              ))}
                              {otherModels.length > 50 && (
                                <div className="px-3 py-2 text-xs text-gray-500">
                                  +{otherModels.length - 50} more models. Use search to filter.
                                </div>
                              )}
                            </>
                          )}

                          <div className="border-t">
                            <div
                              className="px-3 py-2 hover:bg-gray-50 cursor-pointer text-blue-600"
                              onClick={() => {
                                setCustomModel(true)
                                setShowModelDropdown(false)
                              }}
                            >
                              <Text size="2">Enter model ID manually...</Text>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                  <Text size="1" color="gray" mt="1">
                    Optional: Select a default model for AI generation
                  </Text>
                </div>
              </>
            )}

            {/* Optional Fields */}
            {testResult?.isSuccess && optionalFields.length > 0 && (
              <>
                <Separator size="4" />
                <Text size="2" weight="bold" color="gray">Optional Settings</Text>
                {optionalFields.map((field) => (
                  <div key={field.name}>
                    <Text as="label" size="2" weight="medium" mb="1">
                      {field.label}
                    </Text>
                    <TextField.Root
                      type={field.type || 'text'}
                      placeholder={field.placeholder}
                      value={formData[field.name] || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value }))}
                    />
                    {field.helpText && (
                      <Text size="1" color="gray" mt="1">
                        {field.helpText}
                      </Text>
                    )}
                  </div>
                ))}
              </>
            )}

            {/* Error Display */}
            {error && (
              <Flex align="center" gap="2" className="text-red-600">
                <AlertCircle className="h-4 w-4" />
                <Text size="2">{error}</Text>
              </Flex>
            )}
          </Flex>

          <Flex gap="3" mt="4" justify="end">
            <Dialog.Close>
              <Button variant="soft" color="gray" type="button">
                Cancel
              </Button>
            </Dialog.Close>
            <Button
              type="submit"
              disabled={loading || !isPrimaryFieldValid || !testResult?.isSuccess}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4" />
                  Connect
                </>
              )}
            </Button>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  )
}

interface DisconnectModalProps {
  integration: Integration | null
  open: boolean
  onClose: () => void
  onDisconnect: () => Promise<void>
}

function DisconnectModal({ integration, open, onClose, onDisconnect }: DisconnectModalProps) {
  const [loading, setLoading] = useState(false)

  const handleDisconnect = async () => {
    setLoading(true)
    try {
      await onDisconnect()
      onClose()
    } finally {
      setLoading(false)
    }
  }

  return (
    <Dialog.Root open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <Dialog.Content maxWidth="400px">
        <Dialog.Title>Disconnect {integration?.name}?</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          This will remove the API credentials and disable the integration.
          You can reconnect it later with new credentials.
        </Dialog.Description>

        <Flex gap="3" mt="4" justify="end">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Cancel
            </Button>
          </Dialog.Close>
          <Button color="red" onClick={handleDisconnect} disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Disconnecting...
              </>
            ) : (
              <>
                <X className="h-4 w-4" />
                Disconnect
              </>
            )}
          </Button>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  )
}

export default function IntegrationsPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const tenantId = searchParams.get('tenantId')
  const { user: currentUser, hasPermission, hasAnyRole, isLoading: authLoading } = useAuth()

  // Check if user has admin access
  const canAccessPage = currentUser?.isSuperAdmin || hasAnyRole(['Admin', 'Tenant Admin']) || hasPermission('integration.view')

  const [integrations, setIntegrations] = useState<Integration[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [connectModal, setConnectModal] = useState<Integration | null>(null)
  const [disconnectModal, setDisconnectModal] = useState<Integration | null>(null)
  const [testingIntegration, setTestingIntegration] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<{ type: string; result: IntegrationTestResult } | null>(null)
  const [togglingIntegration, setTogglingIntegration] = useState<string | null>(null)

  // Company filter state
  const [availableCompanies, setAvailableCompanies] = useState<TenantOption[]>([])

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Show access denied if user doesn't have permission
  if (!canAccessPage) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-4">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <ShieldAlert className="h-12 w-12 text-destructive" />
        </div>
        <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
        <p className="text-muted-foreground max-w-md mb-6">
          You don't have permission to access the Integrations page. Please contact your administrator if you need access.
        </p>
        <ShadcnButton variant="outline" onClick={() => window.history.back()}>
          Go Back
        </ShadcnButton>
      </div>
    )
  }
  const [companiesLoading, setCompaniesLoading] = useState(false)

  // Check if current user is admin
  const isAdmin = currentUser?.isSuperAdmin || currentUser?.roles?.some(r =>
    r.name === 'Admin' || r.name === 'SuperAdmin' || r.name === 'System Admin'
  )

  // Handle company filter change
  const handleCompanyChange = (value: string) => {
    if (value === 'all') {
      searchParams.delete('tenantId')
    } else {
      searchParams.set('tenantId', value)
    }
    setSearchParams(searchParams)
  }

  // Load available companies for admin users
  useEffect(() => {
    const loadCompanies = async () => {
      if (!isAdmin) return
      try {
        setCompaniesLoading(true)
        const { tenantsService } = await import('@/lib/api/services/tenants')
        const response = await tenantsService.getTenants({ pageSize: 100, sortBy: 'name' })
        const companies = (response.items || []).map((t) => ({
          id: t.id,
          name: t.name
        }))
        setAvailableCompanies(companies)
      } catch {
        // Ignore error
      } finally {
        setCompaniesLoading(false)
      }
    }
    loadCompanies()
  }, [isAdmin])

  useEffect(() => {
    loadIntegrations()
  }, [tenantId])

  const loadIntegrations = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await integrationService.getIntegrations(tenantId || undefined)
      setIntegrations(data)
    } catch (err) {
      setError('Failed to load integrations')
      console.error('Failed to load integrations:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleConnect = async (config: ConnectIntegrationRequest) => {
    if (!connectModal) return
    const updated = await integrationService.connectIntegration(connectModal.type, config)
    setIntegrations(prev =>
      prev.map(int => int.type === connectModal.type ? updated : int)
    )
  }

  const handleDisconnect = async () => {
    if (!disconnectModal) return
    await integrationService.disconnectIntegration(disconnectModal.type)
    await loadIntegrations()
  }

  const handleToggle = async (integration: Integration) => {
    setTogglingIntegration(integration.type)
    try {
      const updated = await integrationService.toggleIntegration(
        integration.type,
        !integration.isEnabled
      )
      setIntegrations(prev =>
        prev.map(int => int.type === integration.type ? updated : int)
      )
    } catch (err) {
      console.error('Failed to toggle integration:', err)
    } finally {
      setTogglingIntegration(null)
    }
  }

  const handleTest = async (integration: Integration) => {
    setTestingIntegration(integration.type)
    setTestResult(null)
    try {
      const result = await integrationService.testIntegration(integration.type)
      setTestResult({ type: integration.type, result })
      await loadIntegrations()
    } catch (err) {
      setTestResult({
        type: integration.type,
        result: {
          isSuccess: false,
          status: 'Error',
          errorMessage: err instanceof Error ? err.message : 'Test failed',
          testedAt: new Date().toISOString(),
        }
      })
    } finally {
      setTestingIntegration(null)
    }
  }

  const groupedIntegrations = integrations.reduce<Record<string, Integration[]>>((acc, int) => {
    if (!acc[int.category]) {
      acc[int.category] = []
    }
    acc[int.category]!.push(int)
    return acc
  }, {})

  const getStatusBadge = (integration: Integration) => {
    switch (integration.status) {
      case 'Connected':
        return (
          <Badge color="green" variant="soft" size="1">
            <Check className="h-3 w-3" />
            Connected
          </Badge>
        )
      case 'Error':
        return (
          <Badge color="red" variant="soft" size="1">
            <AlertCircle className="h-3 w-3" />
            Error
          </Badge>
        )
      default:
        return (
          <Badge color="gray" variant="soft" size="1">
            Not Connected
          </Badge>
        )
    }
  }

  return (
    <>
      <PageMeta
        title="Integrations | Admin"
        description="Manage third-party integrations"
      />

      <Container size="4" className="py-8">
        <Flex direction="column" gap="6">
          {/* Header */}
          <Flex justify="between" align="center">
            <Flex direction="column" gap="1">
              <Heading size="7">Integrations</Heading>
              <Text color="gray" size="2">
                {tenantId
                  ? `Integrations for ${availableCompanies.find(c => c.id === tenantId)?.name || 'selected company'}`
                  : 'Connect third-party services to extend functionality'
                }
              </Text>
            </Flex>
            <Flex gap="3" align="center">
              {/* Company Filter for Admins */}
              {isAdmin && availableCompanies.length > 0 && (
                <Flex align="center" gap="2">
                  <Building2 className="h-4 w-4 text-gray-500" />
                  <RadixSelect.Root
                    value={tenantId || 'all'}
                    onValueChange={handleCompanyChange}
                    disabled={companiesLoading}
                  >
                    <RadixSelect.Trigger style={{ minWidth: '180px' }} />
                    <RadixSelect.Content>
                      <RadixSelect.Item value="all">All Companies</RadixSelect.Item>
                      {availableCompanies.map((company) => (
                        <RadixSelect.Item key={company.id} value={company.id}>
                          {company.name}
                        </RadixSelect.Item>
                      ))}
                    </RadixSelect.Content>
                  </RadixSelect.Root>
                </Flex>
              )}
              <Button variant="soft" onClick={loadIntegrations} disabled={loading}>
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Badge variant="soft" color="blue">
                <Plug className="h-3 w-3" />
                {integrations.filter(i => i.isConnected).length} Connected
              </Badge>
            </Flex>
          </Flex>

          {/* Loading State */}
          {loading && integrations.length === 0 && (
            <Card>
              <Flex direction="column" align="center" gap="3" py="8">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                <Text color="gray">Loading integrations...</Text>
              </Flex>
            </Card>
          )}

          {/* Error State */}
          {error && !loading && (
            <Card>
              <Flex direction="column" align="center" gap="3" py="8">
                <Text color="red">{error}</Text>
                <Button variant="soft" onClick={loadIntegrations}>Retry</Button>
              </Flex>
            </Card>
          )}

          {/* Test Result Toast */}
          {testResult && (
            <Card className={testResult.result.isSuccess ? 'border-green-500 border' : 'border-red-500 border'}>
              <Flex justify="between" align="center">
                <Flex gap="3" align="center">
                  {testResult.result.isSuccess ? (
                    <Check className="h-5 w-5 text-green-600" />
                  ) : (
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  )}
                  <Flex direction="column">
                    <Text weight="medium">
                      {testResult.result.isSuccess ? 'Connection successful' : 'Connection failed'}
                    </Text>
                    {testResult.result.errorMessage && (
                      <Text size="1" color="red">{testResult.result.errorMessage}</Text>
                    )}
                  </Flex>
                </Flex>
                <Button variant="ghost" size="1" onClick={() => setTestResult(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </Flex>
            </Card>
          )}

          {/* Integrations by Category */}
          {!loading && !error && Object.entries(groupedIntegrations).map(([category, items]) => (
            <Flex direction="column" gap="4" key={category}>
              <Heading size="4">{categoryLabels[category] || category}</Heading>
              <Grid columns={{ initial: '1', md: '2' }} gap="4">
                {items.map((integration) => (
                  <Card key={integration.type}>
                    <Flex direction="column" gap="4">
                      <Flex justify="between" align="start">
                        <Flex gap="3" align="center">
                          <div className="text-blue-600">
                            {iconMap[integration.icon] || <Plug className="h-6 w-6" />}
                          </div>
                          <Flex direction="column" gap="1">
                            <Flex align="center" gap="2">
                              <Text weight="medium">{integration.name}</Text>
                              {getStatusBadge(integration)}
                            </Flex>
                            <Text color="gray" size="2">
                              {integration.description}
                            </Text>
                            {integration.lastError && (
                              <Text size="1" color="red">
                                {integration.lastError}
                              </Text>
                            )}
                            {integration.lastTestedAt && (
                              <Text size="1" color="gray">
                                Last tested: {new Date(integration.lastTestedAt).toLocaleString()}
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                      </Flex>

                      <Flex justify="between" align="center">
                        {integration.isConnected ? (
                          <Flex align="center" gap="2">
                            <Switch
                              checked={integration.isEnabled}
                              disabled={togglingIntegration === integration.type}
                              onCheckedChange={() => handleToggle(integration)}
                            />
                            <Text size="2" color="gray">
                              {integration.isEnabled ? 'Enabled' : 'Disabled'}
                            </Text>
                          </Flex>
                        ) : (
                          <div />
                        )}
                        <Flex gap="2">
                          {integration.isConnected && (
                            <>
                              <Button
                                variant="soft"
                                size="2"
                                color="gray"
                                onClick={() => handleTest(integration)}
                                disabled={testingIntegration === integration.type}
                              >
                                {testingIntegration === integration.type ? (
                                  <Loader2 className="h-3 w-3 animate-spin" />
                                ) : (
                                  <TestTube className="h-3 w-3" />
                                )}
                                Test
                              </Button>
                              <Button
                                variant="soft"
                                size="2"
                                color="red"
                                onClick={() => setDisconnectModal(integration)}
                              >
                                Disconnect
                              </Button>
                            </>
                          )}
                          {!integration.isConnected && (
                            <Button
                              variant="solid"
                              size="2"
                              onClick={() => setConnectModal(integration)}
                            >
                              Connect
                            </Button>
                          )}
                        </Flex>
                      </Flex>
                    </Flex>
                  </Card>
                ))}
              </Grid>
            </Flex>
          ))}

          {!loading && !error && integrations.length === 0 && (
            <Card>
              <Flex direction="column" align="center" gap="3" py="8">
                <Plug className="h-12 w-12 text-gray-400" />
                <Text color="gray">No integrations available</Text>
              </Flex>
            </Card>
          )}
        </Flex>
      </Container>

      <ConnectModal
        integration={connectModal}
        open={connectModal !== null}
        onClose={() => setConnectModal(null)}
        onConnect={handleConnect}
      />

      <DisconnectModal
        integration={disconnectModal}
        open={disconnectModal !== null}
        onClose={() => setDisconnectModal(null)}
        onDisconnect={handleDisconnect}
      />
    </>
  )
}
